import React from 'react'
import { 
  Code, 
  Users, 
  MessageCircle, 
  FolderOpen, 
  Settings, 
  DollarSign, 
  Target, 
  Shield, 
  Heart,
  Star
} from 'lucide-react'

interface CategoryData {
  id: string
  name: string
  score: number
  weight: number
  trend?: 'up' | 'down' | 'neutral'
  evidence?: string
  actions?: string[]
}

interface CategoryBreakdownCardProps {
  categories: CategoryData[]
  showWeights?: boolean
  showEvidence?: boolean
  compact?: boolean
}

const CategoryBreakdownCard = ({ 
  categories, 
  showWeights = true, 
  showEvidence = false, 
  compact = false 
}: CategoryBreakdownCardProps) => {
  
  const getCategoryIcon = (id: string) => {
    switch (id) {
      case 'I001':
      case 'technical':
        return Code
      case 'I002':
      case 'communication':
        return MessageCircle
      case 'I003':
      case 'processes':
        return Settings
      case 'I004':
      case 'projects':
        return FolderOpen
      case 'I005':
      case 'finance':
        return DollarSign
      case 'I006':
      case 'behavioral':
        return Users
      case 'I007':
      case 'ethics':
        return Shield
      case 'I008':
      case 'culture':
        return Heart
      case 'I009':
      case 'diversity':
        return Star
      case 'I010':
      case 'client':
        return Target
      default:
        return Target
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success bg-success/10'
    if (score >= 60) return 'text-brand-primary bg-brand-primary/10'
    if (score >= 40) return 'text-warning bg-warning/10'
    return 'text-error bg-error/10'
  }

  const getScoreBarColor = (score: number) => {
    if (score >= 80) return 'bg-success'
    if (score >= 60) return 'bg-brand-primary'
    if (score >= 40) return 'bg-warning'
    return 'bg-error'
  }

  return (
    <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10 hover:border-brand-primary/30 transition-all duration-200 hover:shadow-monumenta-brand">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-display font-semibold text-brand-primary">
          Breakdown por Categoria
        </h3>
        {showWeights && (
          <div className="text-xs text-brand-gray bg-brand-dark/30 px-2 py-1 rounded">
            Pesos aplicados
          </div>
        )}
      </div>

      <div className="space-y-4">
        {categories.map((category, index) => {
          const Icon = getCategoryIcon(category.id)
          
          return (
            <div key={index} className="group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-brand-primary/10 rounded-monumenta group-hover:bg-brand-primary/20 transition-colors duration-200">
                    <Icon className="text-brand-primary" size={16} />
                  </div>
                  <div>
                    <span className="font-display font-medium text-white">
                      {category.name}
                    </span>
                    {showWeights && (
                      <span className="text-xs text-brand-gray ml-2">
                        ({category.weight}%)
                      </span>
                    )}
                  </div>
                </div>
                
                <div className={`px-3 py-1 rounded-full text-sm font-display font-bold ${getScoreColor(category.score)}`}>
                  {category.score}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-brand-dark rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${getScoreBarColor(category.score)}`}
                  style={{ width: `${category.score}%` }}
                ></div>
              </div>

              {/* Evidence and Actions (if not compact) */}
              {!compact && showEvidence && (
                <>
                  {category.evidence && (
                    <div className="text-xs text-brand-gray bg-brand-dark/20 p-2 rounded mb-2">
                      <strong>Evidência:</strong> {category.evidence}
                    </div>
                  )}
                  
                  {category.actions && category.actions.length > 0 && (
                    <div className="text-xs text-brand-gray bg-brand-dark/20 p-2 rounded">
                      <strong>Ações:</strong>
                      <ul className="mt-1 space-y-1">
                        {category.actions.map((action, idx) => (
                          <li key={idx} className="flex items-start space-x-1">
                            <span className="text-brand-primary">•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-brand-gray/20">
        <div className="flex justify-between items-center text-sm">
          <span className="text-brand-gray">
            {categories.length} categorias avaliadas
          </span>
          <span className="text-brand-primary font-display font-medium">
            Média: {(categories.reduce((sum, cat) => sum + cat.score, 0) / categories.length).toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  )
}

export default CategoryBreakdownCard
